import React from "react";
import { Building2, User } from "lucide-react";
import CustomModal from "../../common/CustomModal";
import { Principal } from "../../../features/principal/PrincipalSlice";

interface CreatePrincipalUserModalProps {
  show: boolean;
  onHide: () => void;
  principalId: string;
  name: string;
  email: string;
  password: string;
  onPrincipalIdChange: (value: string) => void;
  onNameChange: (value: string) => void;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onSubmit: () => void;
  loading: boolean;
  error: string | null;
  principalList: Principal[] | null;
}

const inputBase =
  "w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg bg-white placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent transition-colors";

export const CreatePrincipalUserModal: React.FC<CreatePrincipalUserModalProps> = ({
  show,
  onHide,
  principalId,
  name,
  email,
  password,
  onPrincipalIdChange,
  onNameChange,
  onEmailChange,
  onPasswordChange,
  onSubmit,
  loading,
  error,
  principalList,
}) => {
  const isValid =
    principalId && name.trim() && email.trim() && password && !loading;

  return (
    <CustomModal
      show={show}
      onHide={onHide}
      title="Create Principal User"
      width="w-[540px]"
      footer={
        <div className="flex justify-end gap-3 w-full">
          <button
            type="button"
            onClick={onHide}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSubmit}
            disabled={!isValid}
            className="px-5 py-2.5 text-sm font-medium text-white bg-brand rounded-lg hover:bg-brand/90 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-brand disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Creating..." : "Create User"}
          </button>
        </div>
      }
    >
      <div className="w-full max-w-full">
        <div className="px-5 py-4">
          {error && (
          <div className="mb-5 p-3.5 text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg flex items-start gap-2">
            <span className="flex-shrink-0 mt-0.5">!</span>
            <span>{error}</span>
          </div>
        )}

        {/* Section: Select Principal */}
        <section className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-600">
              <Building2 className="w-4 h-4" strokeWidth={2} />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">Principal</h4>
              <p className="text-xs text-gray-500">Choose the principal this user will belong to</p>
            </div>
          </div>
          <div>
            <label htmlFor="create-principal-user-principal" className="sr-only">
              Principal
            </label>
            <select
              id="create-principal-user-principal"
              value={principalId}
              onChange={(e) => onPrincipalIdChange(e.target.value)}
              className={`${inputBase} cursor-pointer`}
              aria-describedby="principal-hint"
            >
              <option value="">Select principal</option>
              {principalList?.map((p) => (
                <option key={p.principalId} value={p.principalId}>
                  {p.name}
                </option>
              ))}
            </select>
            <p id="principal-hint" className="mt-1.5 text-xs text-gray-500">
              Required. The user will have access under this principal.
            </p>
          </div>
        </section>

        {/* Divider */}
        <div className="border-t border-gray-200 my-6" aria-hidden />

        {/* Section: User Details */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 text-gray-600">
              <User className="w-4 h-4" strokeWidth={2} />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900">User details</h4>
              <p className="text-xs text-gray-500">Name, email and password for the new user</p>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="create-principal-user-name" className="block text-sm font-medium text-gray-700 mb-1.5">
                Full name
              </label>
              <input
                id="create-principal-user-name"
                type="text"
                value={name}
                onChange={(e) => onNameChange(e.target.value)}
                placeholder="e.g. John Doe"
                className={inputBase}
                autoComplete="name"
              />
            </div>
            <div>
              <label htmlFor="create-principal-user-email" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email address
              </label>
              <input
                id="create-principal-user-email"
                type="email"
                value={email}
                onChange={(e) => onEmailChange(e.target.value)}
                placeholder="e.g. john@example.com"
                className={inputBase}
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor="create-principal-user-password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <input
                id="create-principal-user-password"
                type="password"
                value={password}
                onChange={(e) => onPasswordChange(e.target.value)}
                placeholder="Enter a secure password"
                className={inputBase}
                autoComplete="new-password"
              />
            </div>
          </div>
        </section>
        </div>
      </div>
    </CustomModal>
  );
};
